import React from 'react'
import { Link, router } from '@inertiajs/react'
import {
  LineChart, Line, ResponsiveContainer
} from 'recharts'
import {
  Target, Activity, TrendingDown, TrendingUp, AlertTriangle, ArrowRight, ChevronRight
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { dormantReasonLabel } from '@/lib/target-utils'
import { formatPoints } from '@/lib/plain-language'
import DashboardCard from '@/components/dashboard/DashboardCard'
import { KeepTargetButton } from '@/components/targets/VerdictAck'
import type { TargetVerdict } from '@/types/shared'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SnapshotSparkPoint {
  computedAt: string
  forecastEom: number | null
}

export interface PrimaryOutlook {
  targetMonth: number
  targetYear: number
  monthName: string
  forecastEom: number | null
  targetEom: number | null
  targetGap: number | null
  state: string | null
  dormantReason: string | null
  monthConfidence: number | null
  capTrimmedPct: number | null
  atFloorPct: number | null
  capTrimmed: boolean
  daysRemaining: number
  computedAt: string
  sparkline: SnapshotSparkPoint[]
  verdict: TargetVerdict | null
}

export interface SecondaryOutlook {
  targetMonth: number
  targetYear: number
  monthName: string
  forecastEom: number | null
  targetEom: number | null
  targetGap: number | null
  state: string | null
  dormantReason: string | null
  monthConfidence: number | null
  capTrimmedPct: number | null
  capTrimmed: boolean
  computedAt: string
  verdict: TargetVerdict | null
}

export interface TargetOutlookData {
  primary: PrimaryOutlook
  secondary: SecondaryOutlook[]
  priorityRule: 'current_month' | 'nearest_future' | 'most_recent_past'
}

interface TargetOutlookCardProps {
  targetOutlook: TargetOutlookData | null
  /** The property's backend-served canonical path. */
  propertyBasePath: string
}

// ── State config ──────────────────────────────────────────────────────────────

const STATE_CONFIG = {
  on_track: {
    label: 'On track',
    badgeClass: 'bg-slate-100 text-slate-500',
    lineColor: '#94a3b8',
    icon: Activity,
  },
  pushing: {
    label: 'Pushing',
    badgeClass: 'bg-rose-100 text-rose-700',
    lineColor: '#f43f5e',
    icon: TrendingDown,
  },
  easing: {
    label: 'Easing',
    badgeClass: 'bg-emerald-100 text-emerald-700',
    lineColor: '#10b981',
    icon: TrendingUp,
  },
  at_floor: {
    label: 'At floor',
    badgeClass: 'bg-amber-100 text-amber-700',
    lineColor: '#f59e0b',
    icon: AlertTriangle,
  },
  dormant: {
    label: 'Dormant',
    badgeClass: 'bg-slate-100 text-slate-700',
    lineColor: '#94a3b8',
    icon: Activity,
  },
} as const

type StateKey = keyof typeof STATE_CONFIG

function getStateCfg(state: string | null) {
  if (!state || !(state in STATE_CONFIG)) return STATE_CONFIG.dormant
  return STATE_CONFIG[state as StateKey]
}

// ── Cap-trimmed sub-indicator ─────────────────────────────────────────────────

function CapTrimmedPill() {
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-semibold">
      Trimmed by demand
    </span>
  )
}

// ── Verdict — one per month, resolved server-side ─────────────────────────────
//
// This component never picks a winner among the alert conditions, ranks severities, or writes
// copy: `ResolveTargetVerdictMutation` does all three, so the dashboard and the Targets page
// cannot tell an operator two different stories about the same month. Rendered here on the
// month it describes — which is what the old layout banner rail could not do, because it
// rendered a bare title and two months' worth of alerts came out as identical sentences.
//
// Only `attention` and `watch` get a block. A `quiet` verdict (dormant, on track) is already
// covered by the narration line above it, and a box repeating it would be the same noise in a
// new place.

const VERDICT_STYLE = {
  attention: {
    container: 'border-amber-200 bg-amber-50',
    headline: 'text-amber-900',
    detail: 'text-amber-800/80',
    credibility: 'text-amber-800/60',
    icon: 'text-amber-500',
  },
  watch: {
    container: 'border-border/60 bg-muted/40',
    headline: 'text-foreground',
    detail: 'text-muted-foreground',
    credibility: 'text-muted-foreground/80',
    icon: 'text-muted-foreground/60',
  },
  quiet: {
    container: 'border-border/50 bg-muted/20',
    headline: 'text-muted-foreground',
    detail: 'text-muted-foreground',
    credibility: 'text-muted-foreground/80',
    icon: 'text-muted-foreground/40',
  },
} as const

function showsVerdict(verdict: TargetVerdict | null): verdict is TargetVerdict {
  return verdict != null && (verdict.severity === 'attention' || verdict.severity === 'watch')
}

function VerdictChip({ verdict }: { verdict: TargetVerdict }) {
  if (!verdict.chipLabel) return null
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-semibold',
        verdict.severity === 'attention' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600',
      )}
    >
      {verdict.chipLabel}
    </span>
  )
}

function VerdictBlock({ verdict, propertyBasePath }: { verdict: TargetVerdict; propertyBasePath: string }) {
  const style = VERDICT_STYLE[verdict.severity]
  const Icon = verdict.severity === 'attention' ? AlertTriangle : Activity

  return (
    <div className={cn('mt-4 rounded-xl border p-3.5', style.container)}>
      <div className="flex gap-2.5">
        <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', style.icon)} />
        <div className="min-w-0 flex-1">
          <p className={cn('text-sm font-semibold leading-snug', style.headline)}>{verdict.headline}</p>
          <p className={cn('mt-1 text-xs leading-relaxed', style.detail)}>{verdict.detail}</p>

          {/* The evidence behind the claim, in a footnote's voice: one step down the type scale
              and a shade quieter than the detail, so it reads as a citation rather than a second
              argument. Backend-composed — rendered verbatim, never assembled here. */}
          {verdict.credibility && (
            <p className={cn('mt-1.5 text-[11px] leading-relaxed', style.credibility)}>{verdict.credibility}</p>
          )}

          {/* Three tiers, deliberately: take the advice (solid), adjust the guardrail that is
              binding (bordered), keep your target (bare text). The ack is always available and
              never advertised — an operator's first option should still be the recommendation. */}
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            {/* Seeds the Targets draft with the suggested number — it does not save. The
                operator still sees the impact preview and presses Save & deploy. */}
            {verdict.recommendation && (
              <Link
                href={verdict.recommendation.href}
                className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                {verdict.recommendation.label}
                <ArrowRight className="h-3 w-3" />
              </Link>
            )}
            {verdict.actionLink && (
              <Link
                href={verdict.actionLink.href}
                className="inline-flex items-center gap-1 rounded-lg border border-border/60 bg-white px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted/50"
              >
                {verdict.actionLink.label}
              </Link>
            )}
            <KeepTargetButton
              propertyBasePath={propertyBasePath}
              month={verdict.targetMonth}
              monthName={verdict.monthName}
              tone={verdict.severity === 'attention' ? 'amber' : 'muted'}
              onDone={() => router.reload({ only: ['targetOutlook'] })}
            />
          </div>

          {/* The conditions that lost the precedence ladder — the "why", one click away and
              never on the glance surface. */}
          {verdict.supporting.length > 0 && (
            <details className="group mt-2.5">
              <summary className="inline-flex cursor-pointer list-none items-center gap-1 rounded text-[11px] font-medium text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/40">
                <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                What else the engine noticed ({verdict.supporting.length})
              </summary>
              <ul className="mt-2 space-y-2 border-l border-border/50 pl-3">
                {verdict.supporting.map((s) => (
                  <li key={s.id}>
                    <p className="text-[11px] font-medium text-foreground">{s.title}</p>
                    <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">{s.body}</p>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Target gap display ────────────────────────────────────────────────────────

function GapDisplay({ gap }: { gap: number | null }) {
  if (gap == null) return <span className="text-muted-foreground">—</span>
  const isPositive = gap >= 0
  return (
    <span className={cn(
      'text-sm font-semibold tabular-nums',
      isPositive ? 'text-emerald-600' : 'text-rose-600'
    )}>
      {formatPoints(Math.round(gap * 10) / 10)}
    </span>
  )
}

// ── Narration line ────────────────────────────────────────────────────────────

function narrationFor(primary: PrimaryOutlook): string {
  const { state, forecastEom, targetEom, dormantReason, daysRemaining } = primary
  const eom = forecastEom != null ? `${forecastEom.toFixed(1)}%` : '—'
  const target = targetEom != null ? `${targetEom.toFixed(1)}%` : '—'
  const days = `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining`

  switch (state) {
    case 'pushing':
      return `Projected end-of-month ${eom} vs target ${target} — pushing harder on remaining dates (${days})`
    case 'easing':
      return `Projected end-of-month ${eom} vs target ${target} — easing off to preserve rate (${days})`
    case 'at_floor':
      return `Projected end-of-month ${eom} vs target ${target} — rates at floor, engine cannot push further`
    case 'on_track':
      return `Projected end-of-month ${eom} vs target ${target} — inside noise floor, no action needed`
    case 'dormant':
      return dormantReasonLabel(dormantReason)
    default:
      return `Projected end-of-month ${eom} vs target ${target}`
  }
}

// ── Sparkline ─────────────────────────────────────────────────────────────────

function Sparkline({ data, color }: { data: SnapshotSparkPoint[]; color: string }) {
  const chartData = data.map((p) => ({ value: p.forecastEom ?? null }))
  return (
    <ResponsiveContainer width="100%" height={50}>
      <LineChart data={chartData} margin={{ top: 4, right: 0, bottom: 4, left: 0 }}>
        <Line
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={1.5}
          dot={false}
          connectNulls
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ propertyBasePath }: { propertyBasePath: string }) {
  return (
    <DashboardCard
      title="Target Outlook"
      icon={<Target className="h-4 w-4 shrink-0 text-muted-foreground" />}
      help={{
        content:
          "Projected end-of-month vs your saved targets. Shows the engine's forecast trajectory and current steering state.",
        learnMore: '/guides/targets',
      }}
    >
      <div className="rounded-xl border border-dashed border-border/60 p-6 text-center">
        <Target className="mx-auto h-7 w-7 text-muted-foreground/30" />
        <p className="mt-2.5 text-sm font-medium text-foreground">No target outlook yet</p>
        <p className="mt-1 text-xs text-muted-foreground max-w-xs mx-auto leading-relaxed">
          Save monthly targets to let the engine compare projected end-of-month against your target.
        </p>
        <div className="mt-3">
          <Link
            href={`${propertyBasePath}/targets`}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
          >
            Set targets
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </DashboardCard>
  )
}

// ── Secondary row ─────────────────────────────────────────────────────────────

function SecondaryRow({ snap, propertyBasePath }: { snap: SecondaryOutlook; propertyBasePath: string }) {
  const cfg = getStateCfg(snap.state)
  const StateIcon = cfg.icon

  return (
    <Link
      href={`${propertyBasePath}/targets/${snap.targetMonth}`}
      className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-muted/40 transition-colors group"
    >
      <span className="w-24 shrink-0 text-sm font-medium text-foreground">{snap.monthName}</span>
      <span className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold shrink-0',
        cfg.badgeClass
      )}>
        <StateIcon className="h-2.5 w-2.5" />
        {cfg.label}
      </span>
      <span className="text-sm tabular-nums text-muted-foreground shrink-0">
        {snap.forecastEom != null ? `${snap.forecastEom.toFixed(1)}%` : '—'}
        {snap.targetEom != null ? ` vs ${snap.targetEom.toFixed(1)}%` : ''}
      </span>
      {showsVerdict(snap.verdict) && <VerdictChip verdict={snap.verdict} />}
      <div className="ml-auto shrink-0">
        <GapDisplay gap={snap.targetGap} />
      </div>
      {snap.capTrimmed && <CapTrimmedPill />}
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </Link>
  )
}

// ── Main card ─────────────────────────────────────────────────────────────────

export default function TargetOutlookCard({ targetOutlook, propertyBasePath }: TargetOutlookCardProps) {
  if (!targetOutlook) return <EmptyState propertyBasePath={propertyBasePath} />

  const { primary, secondary } = targetOutlook
  const cfg = getStateCfg(primary.state)
  const StateIcon = cfg.icon
  const narration = narrationFor(primary)

  return (
    <DashboardCard
      title="Target Outlook"
      icon={<Target className="h-4 w-4 shrink-0 text-muted-foreground" />}
      help={{
        content:
          "Projected end-of-month vs your saved targets. Shows the engine's forecast trajectory and current steering state.",
        learnMore: '/guides/targets',
      }}
      action={
        <Link
          href={`${propertyBasePath}/targets/${primary.targetMonth}`}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:text-primary/80 transition-colors"
        >
          View details
          <ArrowRight className="h-3 w-3" />
        </Link>
      }
    >
      <>
        <div className="flex items-start gap-4">
          {/* Primary content */}
          <div className="flex-1 min-w-0">
            {/* Month + state */}
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <span className="text-sm font-semibold text-foreground">{primary.monthName}</span>
              <span className={cn(
                'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold',
                cfg.badgeClass
              )}>
                <StateIcon className="h-3 w-3" />
                {cfg.label}
              </span>
              {primary.capTrimmed && <CapTrimmedPill />}
            </div>

            {/* Big projected EOM number */}
            <div className="flex items-baseline gap-3 mb-1">
              <span className={cn(
                'text-3xl font-bold tabular-nums',
                primary.state === 'dormant' ? 'text-muted-foreground' : 'text-foreground'
              )}>
                {primary.forecastEom != null ? `${primary.forecastEom.toFixed(1)}%` : '—'}
              </span>
              {primary.targetEom != null && (
                <span className="text-sm text-muted-foreground tabular-nums">
                  target {primary.targetEom.toFixed(1)}%
                </span>
              )}
            </div>

            {/* Gap + horizon */}
            <div className="flex items-center gap-3 mb-3">
              <GapDisplay gap={primary.targetGap} />
              {primary.daysRemaining > 0 && (
                <span className="text-xs text-muted-foreground tabular-nums">
                  {primary.daysRemaining} days remaining
                </span>
              )}
            </div>

            {/* Narration */}
            <p className="text-xs text-muted-foreground leading-relaxed">{narration}</p>
          </div>

          {/* Sparkline */}
          {primary.sparkline.length > 1 && (
            <div className="w-28 shrink-0 mt-1">
              <Sparkline data={primary.sparkline} color={cfg.lineColor} />
              <p className="text-center text-[10px] text-muted-foreground/50 mt-0.5">
                {primary.sparkline.length} snapshot{primary.sparkline.length === 1 ? '' : 's'}
              </p>
            </div>
          )}
        </div>

        {/* The primary month's verdict — full width, under the figures it explains */}
        {showsVerdict(primary.verdict) && <VerdictBlock verdict={primary.verdict} propertyBasePath={propertyBasePath} />}

        {/* Secondary list */}
        {secondary.length > 0 && (
          <div className="mt-4 border-t border-border/40 pt-3 space-y-0.5">
            <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Other saved months
            </p>
            {secondary.map((snap) => (
              <SecondaryRow key={`${snap.targetYear}-${snap.targetMonth}`} snap={snap} propertyBasePath={propertyBasePath} />
            ))}
          </div>
        )}
      </>
    </DashboardCard>
  )
}
